import { useEffect, useState } from 'react'
import { startOfDay } from 'date-fns'

/**
 * Returns a Date representing the start of "today" in local time, and keeps it
 * fresh so that date-picker min-date constraints and "in the past" checks do
 * not go stale when a form is left open across midnight.
 *
 * The value is refreshed when:
 *   - the next local-minute boundary is crossed (cheap one-shot timer that
 *     re-arms itself),
 *   - the tab becomes visible again (covers laptop-sleep / background tab),
 *   - the window regains focus.
 *
 * On each refresh we only call `setState` when the day has actually changed,
 * so components do not re-render every minute during normal use.
 */
export function useTodayMidnight(): Date {
    const [today, setToday] = useState<Date>(() => startOfDay(new Date()))

    useEffect(() => {
        let timerId: ReturnType<typeof setTimeout> | undefined

        const refresh = () => {
            const next = startOfDay(new Date())
            setToday((prev) => (prev.getTime() === next.getTime() ? prev : next))
        }

        const scheduleNextMinute = () => {
            const now = new Date()
            const msUntilNextMinute =
                60_000 - (now.getSeconds() * 1000 + now.getMilliseconds())
            timerId = setTimeout(() => {
                refresh()
                scheduleNextMinute()
            }, msUntilNextMinute)
        }

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') refresh()
        }

        scheduleNextMinute()
        window.addEventListener('focus', refresh)
        document.addEventListener('visibilitychange', handleVisibility)

        return () => {
            if (timerId !== undefined) clearTimeout(timerId)
            window.removeEventListener('focus', refresh)
            document.removeEventListener('visibilitychange', handleVisibility)
        }
    }, [])

    return today
}
